'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { usePathname } from 'next/navigation'

import { FlowChrome } from '@/components/site/layout/flow-chrome'
import { HeroSimpleCentered } from '@/components/site/sections/hero-simple-centered'
import { Link } from '@/components/ui/link'
import { getPagePath, isLocale, type Locale, type PageKey } from '@/i18n/routing'

/** Everything one language needs to draw this page. */
export type NotFoundCopy = {
  headline: string
  subheadline: string
  backToHome: string
  legal: { copyright: string; terms: string; privacy: string }
  /** Label per destination in the "try one of these" row. */
  links: { key: Exclude<PageKey, 'home'>; label: string }[]
}

/** The four places a lost visitor most plausibly meant to be. */
const SUGGESTED: Exclude<PageKey, 'home'>[] = ['planning', 'time-tracking', 'pricing', 'blog']

/**
 * The 404, in the language of the URL that produced it.
 *
 * The locale has to be read on the client, which is not the obvious choice and deserves the
 * explanation: a `not-found` boundary is handed no `params`, and — verified by instrumenting it —
 * `headers()` inside one returns an empty set even with `connection()`, because Next renders the
 * boundary while prerendering rather than per request. So neither the route nor the `x-locale`
 * header the middleware sets can reach it. The path can: it is right there in the address bar, and
 * this page is client-rendered anyway.
 *
 * The surrounding `[locale]/layout.tsx` *does* get the param, so `<html lang>` is already correct
 * before this component runs.
 */
export function NotFoundView({ copy }: { copy: Record<Locale, NotFoundCopy> }) {
  const pathname = usePathname()
  const segment = pathname.split('/')[1] ?? ''
  const locale: Locale = isLocale(segment) ? segment : 'nl'
  const t = copy[locale]

  return (
    <FlowChrome
      locale={locale}
      prompt=""
      linkLabel={t.backToHome}
      linkHref={getPagePath(locale, 'home')}
      legal={t.legal}
    data-sentry-element="FlowChrome" data-sentry-component="NotFoundView" data-sentry-source-file="not-found-view.tsx">
      <HeroSimpleCentered
        headline={t.headline}
        subheadline={<p>{t.subheadline}</p>}
        cta={
          // A way back is the minimum; a lost visitor is better served by the handful of pages they
          // probably meant, which is why these sit here rather than a full navigation bar.
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
            {SUGGESTED.map((key) => {
              const label = t.links.find((link) => link.key === key)?.label
              return label ? (
                <Link
                  key={key}
                  href={getPagePath(locale, key)}
                  unstyled
                  className="text-sm/7 font-medium text-accent-deep dark:text-accent-bright"
                >
                  {label} ›
                </Link>
              ) : null
            })}
          </div>
        }
      data-sentry-element="HeroSimpleCentered" data-sentry-source-file="not-found-view.tsx" />
    </FlowChrome>
  )
}
